import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { useProAuth, readProPreviewSession, clearProPreviewSession } from '@/lib/auth-context';

/**
 * Sticky banner shown when the current session was opened from the dashboard as
 * an owner preview / electrician impersonation (see consumeProHandoffFromUrl).
 * Makes the impersonation obvious and offers a one-click exit (logout + close).
 */
export function PreviewBanner() {
  const { t } = useTranslation();
  const { logout } = useProAuth();
  const preview = readProPreviewSession();
  if (!preview) return null;

  const label = preview.kind === 'impersonate'
    ? t('pro.preview.impersonate', 'Mode aperçu — vous consultez en tant que {{name}}', {
        name: preview.targetName ?? t('pro.preview.thisElectrician', 'cet électricien'),
      })
    : // admin_preview is the owner's OWN electrician account (real, listed) —
      // everything he does here (commandes, devis…) is live, like any pro.
      t('pro.preview.owner', 'Connecté avec votre compte électricien{{name}} — vos actions sont réelles', {
        name: preview.targetName ? ` (${preview.targetName})` : '',
      });

  const onExit = async () => {
    clearProPreviewSession();
    try {
      await logout();
    } catch {
      /* ignore — we still close/redirect below */
    }
    // The preview opens in a script-opened tab, so close() returns the owner to
    // the dashboard tab; if the browser blocks it, fall back to the login page.
    window.close();
    window.location.href = '/login';
  };

  return (
    <div className="sticky top-0 z-[200] flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-center text-[13px] font-medium text-white">
      <Eye className="hidden h-4 w-4 shrink-0 sm:block" />
      <span>{label}</span>
      <button
        type="button"
        onClick={onExit}
        className="shrink-0 rounded-md bg-white/20 px-3 py-1 text-[12px] font-semibold transition-colors hover:bg-white/30"
      >
        {t('pro.preview.exit', 'Quitter l’aperçu')}
      </button>
    </div>
  );
}
